'use client';

import { closeIcon } from '@/uiIcons/closeIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { produce } from '@/globals/helpers/immer';
import { usePathname } from 'next/navigation';
import {
	ButtonHTMLAttributes,
	DetailsHTMLAttributes,
	Fragment,
	InputHTMLAttributes,
	PropsWithChildren,
	createContext,
	startTransition,
	useContext,
	useMemo,
	useOptimistic,
	useState,
} from 'react';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { filterClearToggleCSS } from './filter.css';
import { SelectedFiltersMap, getSelectedFilters } from './getSelectedFilters';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { rows } from '@/uiPrimitives/layout/rows';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { pushInternalLinkEvent } from '@/globals/analytics/events/pushLinkEvent';
import { WebAPIOptionFilter } from '@/globals/webApiTypes/elements/WebAPIEvent';
import { translateInline } from '@/globals/translate/translateInline';

type OptimisticSelectedFilters = ReturnType<
	typeof useOptimistic<
		SelectedFiltersMap,
		Parameters<typeof updateSelectedFilters>[1]
	>
>;
const FilterStateContext = createContext<OptimisticSelectedFilters | undefined>(
	undefined,
);

const useGetSelectedFilters = (groupKeys: Array<string>) => {
	const pathname = usePathname();

	return useMemo(() => {
		const segments = pathname.split('/');

		return getSelectedFilters(groupKeys, segments);
	}, [groupKeys, pathname]);
};

const updateSelectedFilters = produce(
	(
		selectedFiltersDraft: SelectedFiltersMap,
		{
			groupKey,
			optionKey,
			checked,
		}: { groupKey?: string; optionKey?: string; checked: boolean },
	) => {
		if (groupKey == null) {
			if (!checked) selectedFiltersDraft.clear();
			return;
		}
		const filterGroup = selectedFiltersDraft.get(groupKey);

		if (filterGroup == null) return;

		if (optionKey == null) {
			if (!checked) filterGroup.clear();
			return;
		}

		if (checked) {
			filterGroup.add(optionKey);
		} else {
			filterGroup.delete(optionKey);
		}
	},
);

const getNewPathname = ({
	oldSelectedFilters,
	basePathname,
	groupKey,
	optionKey,
	checked,
}: {
	oldSelectedFilters: SelectedFiltersMap;
	basePathname: string;
	groupKey?: string;
	optionKey?: string;
	checked: boolean;
}) => {
	const selectedFilters = updateSelectedFilters(oldSelectedFilters, {
		groupKey,
		optionKey,
		checked,
	});

	const segments: Array<string> = [basePathname];

	for (const [groupKey, options] of selectedFilters) {
		if (options.size === 0) continue;

		segments.push(groupKey, ...options);
	}

	return segments.join('/').replace(/\/+/g, '/');
};

export const SelectedFiltersProvider = ({
	children,
	groupKeys,
}: PropsWithChildren<{ groupKeys: Array<string> }>) => {
	const selectedFilters = useGetSelectedFilters(groupKeys);
	const optimisticSelectedFilters = useOptimistic(
		selectedFilters,
		updateSelectedFilters,
	);
	return (
		<FilterStateContext.Provider value={optimisticSelectedFilters}>
			{children}
		</FilterStateContext.Provider>
	);
};

export const useSelectedFilters = () => {
	const value = useContext(FilterStateContext);

	if (value == null) {
		throw new Error(
			'useSelectedFilters must be used within a SelectedFiltersProvider',
		);
	}

	return value;
};

export const FilterCheckbox = ({
	optionKey,
	groupKey,
	basePathname,
	...props
}: {
	basePathname: string;
	optionKey: string;
	groupKey: string;
} & InputHTMLAttributes<HTMLInputElement>) => {
	const [selectedFilters, updateSelectedFilters] = useSelectedFilters();
	const checked = selectedFilters.get(groupKey)?.has(optionKey) ?? false;
	const router = useDOPRouter();
	return (
		<input
			type="checkbox"
			{...props}
			checked={checked}
			onChange={({ currentTarget }) => {
				const checked = currentTarget.checked;
				startTransition(() => {
					updateSelectedFilters({
						groupKey,
						optionKey,
						checked,
					});
					const href = getNewPathname({
						oldSelectedFilters: selectedFilters,
						basePathname,
						groupKey,
						optionKey,
						checked,
					});
					router.replace(href, { scroll: false });
					pushInternalLinkEvent({
						href,
						linkType: 'filterChanged',
					});
				});
			}}
		/>
	);
};

export const ClearFilter = ({
	optionKey,
	groupKey,
	basePathname,
	...props
}: {
	basePathname: string;
	optionKey?: string;
	groupKey?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) => {
	const [selectedFilters, updateSelectedFilters] = useSelectedFilters();
	const router = useDOPRouter();

	return (
		<button
			type="button"
			{...props}
			onClick={() => {
				startTransition(() => {
					updateSelectedFilters({
						groupKey,
						optionKey,
						checked: false,
					});
					const href = getNewPathname({
						oldSelectedFilters: selectedFilters,
						basePathname,
						groupKey,
						optionKey,
						checked: false,
					});
					router.replace(href, { scroll: false });
					pushInternalLinkEvent({
						href,
						linkType: 'filterChanged',
					});
				});
			}}
		/>
	);
};

export const FilterClearToggle = ({
	groupKey,
	basePathname,
	children,
	...props
}: {
	basePathname: string;
	groupKey: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) => {
	const [selectedFilters, updateSelectedFilters] = useSelectedFilters();
	const router = useDOPRouter();
	const count = selectedFilters.get(groupKey)?.size ?? 0;

	return (
		<button
			type="button"
			{...props}
			aria-pressed={count === 0}
			className={classes(shelf({}))}
			onClick={
				count > 0
					? () => {
							startTransition(() => {
								updateSelectedFilters({
									groupKey,
									checked: false,
								});
								const href = getNewPathname({
									oldSelectedFilters: selectedFilters,
									basePathname,
									groupKey,
									checked: false,
								});
								router.replace(href, { scroll: false });
								pushInternalLinkEvent({
									href,
									linkType: 'filterChanged',
								});
							});
						}
					: undefined
			}
			{...addTestSelector(`uiClearFilterGroup-${groupKey}`)}
		>
			<span
				className={classes(
					occupy({
						inlineSize: indentSize,
						justifyItems: 'start',
					}),
				)}
			>
				<span
					className={classes(
						occupy({
							inlineSize: 'capSize',
							blockSize: 'capSize',
						}),
					)}
				>
					<i
						className={classes(
							box({
								inlineSize: '0 | p',
								blockSize: '0 | p',
								borderStyle: 'solid',
								borderWidth: '1px',
								borderRadius: 'circle',
							}),
							filterClearToggleCSS,
						)}
					/>
				</span>
			</span>
			<span className={classes(textBlock({ color: 'positive:noHover' }))}>
				{children}
			</span>
		</button>
	);
};

export const FilterDetails = ({
	groupKey,
	...props
}: {
	groupKey: string;
} & DetailsHTMLAttributes<HTMLDetailsElement>) => {
	const [selectedFilters] = useSelectedFilters();
	// Only calculate it once
	const [count] = useState(() => selectedFilters.get(groupKey)?.size ?? 0);

	return <details {...props} open={count > 0} />;
};

const getFilterOptionText = ({
	filterTypes,
	groupKey,
	optionKey,
}: {
	filterTypes: Array<WebAPIOptionFilter>;
	groupKey: string;
	optionKey: string;
}) => {
	const group = filterTypes.find((filterType) => {
		return filterType.key === groupKey;
	});
	const option = group?.optionList.find((option) => {
		return option.id === optionKey;
	});

	return option?.text;
};

export const SelectedFilters = ({
	basePathname,
	filterTypes,
	className,
}: {
	basePathname?: string;
	filterTypes?: Array<WebAPIOptionFilter>;
	className?: string;
}) => {
	const [selectedFilters] = useSelectedFilters();
	const hasSelectedFilters = useHasSelectedFilters();

	if (!hasSelectedFilters) return null;

	if (basePathname == null) return null;
	if (filterTypes == null) return null;

	return (
		<div
			className={classes(
				shelf({
					gap: '5 | betweenParagraphs',
					gridTemplateColumns: 'minmax(0, 1fr) auto',
					alignContent: 'start',
				}),
				box({
					inlineSize: 'fit-content',
					maxInlineSize: '100%',
				}),
				className,
			)}
		>
			<div
				className={classes(
					rows({
						columnGap: '-1 | small',
						rowGap: '0 | p',
						alignItems: 'center',
					}),
				)}
			>
				{[...selectedFilters].map(([groupKey, optionKeys]) => {
					return (
						<Fragment key={groupKey}>
							{[...optionKeys].map((optionKey) => {
								const filterLabel = getFilterOptionText({
									filterTypes,
									groupKey,
									optionKey,
								});
								const isValid = filterLabel != null;
								return (
									<ClearFilter
										key={optionKey}
										groupKey={groupKey}
										optionKey={optionKey}
										basePathname={basePathname}
										className={classes(
											box({
												borderStyle: 'solid',
												borderColor: isValid ? 'positiveBlue' : 'positiveRed',
												borderWidth: '2px',
												paddingInline: '-1 | small',
												paddingBlock: '-2 | smaller',
												inlineSize: 'fit-content',
												borderRadius: 'circle',
											}),

											textStyle({
												capSize: '-1 | small',
												color: isValid ? 'positiveBlue' : 'positiveRed',
											}),
										)}
										{...addTestSelector(`uiClearFilter-${optionKey}`)}
									>
										<span
											className={classes(
												shelf({ gap: '-2 | smaller', alignItems: 'center' }),
											)}
										>
											<span
												className={classes(textBlock({ textAlign: 'center' }))}
											>
												{isValid
													? filterLabel
													: `${translateInline({ en: 'Invalid', nl: 'Ongeldig' })}: ${optionKey}`}
											</span>
											<i
												className={classes(
													icon({ icon: closeIcon, blockSize: 'capSize' }),
												)}
											/>
										</span>
									</ClearFilter>
								);
							})}
						</Fragment>
					);
				})}

				<ClearFilter
					basePathname={basePathname}
					className={classes(
						textBlock({
							capSize: '-1 | small',
							color: 'positiveBlue',
							textDecorationLine: 'underline',
						}),
					)}
					{...addTestSelector('uiClearAllFiltersButton')}
				>
					{translateInline({
						en: 'Clear all filters',
						nl: 'Alle filters wissen',
					})}
				</ClearFilter>
			</div>
		</div>
	);
};

export const FilterGroupSelectionCount = ({
	groupKey,
}: {
	groupKey: string;
}) => {
	const [selectedFilters] = useSelectedFilters();
	const count = selectedFilters.get(groupKey)?.size ?? 0;

	if (count === 0) return null;

	return (
		<>
			{'('}
			{count}
			{')'}
		</>
	);
};

export const useHasSelectedFilters = () => {
	const [selectedFilters] = useSelectedFilters();

	for (const selectedFilter of selectedFilters.values()) {
		if (selectedFilter.size > 0) return true;
	}

	return false;
};

export const useCountSelectedFilters = () => {
	const [selectedFilters] = useSelectedFilters();
	let count = 0;

	for (const selectedFilter of selectedFilters.values()) {
		count += selectedFilter.size;
	}

	return count;
};
